import { useCallback, useEffect, useMemo, useState } from "react";
import {
    BarChart3,
    Store,
    Info,
    ArrowUp,
    ArrowDown,
    ChevronsUpDown,
    Clock3,
} from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";
import { useI18n } from "@/context/I18nContext";
import { useAuth } from "@/context/AuthContext";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from "@/components/ui/table";
import { PageHeader } from "@/components/layout/PageHeader";
import { EmptyState } from "@/components/common/EmptyState";
import { fmtByUnit, currencySuffix, lastDayOfMonth } from "@/lib/format";
import { OPS } from "@/constants/testIds";

const C = OPS.branchKpi;

function todayIso() {
    return new Date(Date.now() + 3 * 3600 * 1000).toISOString().slice(0, 10);
}

function buildMonths(latest, count = 12) {
    const base = latest || todayIso().slice(0, 7);
    let y = Number(base.slice(0, 4));
    let m = Number(base.slice(5, 7));
    const out = [];
    for (let i = 0; i < count; i++) {
        out.push(`${y}-${String(m).padStart(2, "0")}`);
        m -= 1;
        if (m < 1) { m = 12; y -= 1; }
    }
    return out;
}

function monthLabel(ym, lang) {
    if (!ym) return "";
    const [yy, mm] = ym.split("-").map(Number);
    return new Date(Date.UTC(yy, mm - 1, 1)).toLocaleDateString(
        lang === "ar" ? "ar-SA" : "en-US",
        { month: "long", year: "numeric" }
    );
}

function fmtCell(cell, unit, lang) {
    if (!cell || !cell.available) return null;
    const s = fmtByUnit(cell.value, unit, lang);
    return unit === "currency" ? `${s} ${currencySuffix(lang)}` : s;
}

// info icon carrying the data lineage (source) + any honesty note as a tooltip
function SourceTip({ cell, t }) {
    if (!cell) return null;
    const parts = [];
    if (cell.source) parts.push(`${t("bkpi_source")}: ${cell.source}`);
    if (cell.note) parts.push(t(`bkpi_note_${cell.note}`));
    if (!parts.length) return null;
    const flagged = cell.note === "pos_not_carrying_discounts";
    return (
        <span title={parts.join("  •  ")} className="cursor-help">
            <Info className={`h-3.5 w-3.5 ${flagged ? "text-[color:var(--brand-brown)]" : "text-[color:var(--ink-faint)]"}`} />
        </span>
    );
}

function KpiCard({ metaItem, cell, t, lang }) {
    const key = metaItem.key;
    const unit = metaItem.unit;
    const flagged = cell?.note === "pos_not_carrying_discounts";
    const display = fmtCell(cell, unit, lang);
    return (
        <Card data-testid={C.card(key)} className="rounded-2xl border-[color:var(--border-soft)] shadow-[var(--shadow-sm)]">
            <CardContent className="p-4 space-y-1.5">
                <div className="flex items-start justify-between gap-2">
                    <p className="text-xs font-medium text-[color:var(--ink-muted)] leading-snug">{t(`bkpi_kpi_${key}`)}</p>
                    <SourceTip cell={cell} t={t} />
                </div>
                <p data-testid={C.cardValue(key)} className="tnum text-2xl font-extrabold">
                    {display === null ? (
                        <span title={t("bkpi_no_data_reason")} className="text-base font-medium text-[color:var(--ink-faint)]">—</span>
                    ) : (
                        <span className="text-[color:var(--ink)]">{display}</span>
                    )}
                </p>
                {cell?.note ? (
                    <p className={`text-[11px] leading-snug ${flagged ? "text-[color:var(--brand-brown)]" : "text-[color:var(--ink-faint)]"}`}>
                        {t(`bkpi_note_${cell.note}`)}
                    </p>
                ) : null}
            </CardContent>
        </Card>
    );
}

function SortHeader({ colKey, label, sort, onSort, testId, align = "end" }) {
    const active = sort.col === colKey;
    const Icon = !active ? ChevronsUpDown : sort.dir === "asc" ? ArrowUp : ArrowDown;
    return (
        <TableHead
            data-testid={testId}
            onClick={() => onSort(colKey)}
            className={`cursor-pointer select-none whitespace-nowrap ${align === "start" ? "text-start" : "text-end"}`}
        >
            <span className={`inline-flex items-center gap-1 ${align === "end" ? "flex-row-reverse" : ""}`}>
                <Icon className={`h-3.5 w-3.5 ${active ? "text-[color:var(--brand-orange)]" : "text-[color:var(--ink-faint)]"}`} />
                {label}
            </span>
        </TableHead>
    );
}

export default function BranchKpiPage() {
    const { t, lang, tn } = useI18n();
    const { isBranchManager } = useAuth();
    const [scope, setScope] = useState(null);
    const [loading, setLoading] = useState(true);
    const [view, setView] = useState("single");
    const [month, setMonth] = useState(todayIso().slice(0, 7));
    const [date, setDate] = useState(todayIso());
    const [branchId, setBranchId] = useState("");
    const [single, setSingle] = useState(null);
    const [compare, setCompare] = useState(null);
    const [sort, setSort] = useState({ col: "monthly_revenue", dir: "desc" });

    const meta = useMemo(() => scope?.kpi_meta || [], [scope]);
    // Anchor the picker on the OPEN month (today), not on the newest month that
    // happens to have data — otherwise the current and not-yet-pulled months
    // silently vanish from the list.
    const months = useMemo(() => {
        const anchor = [scope?.current_month, scope?.latest_month, todayIso().slice(0, 7)]
            .filter(Boolean)
            .sort()
            .pop();
        return buildMonths(anchor, 14);
    }, [scope]);
    const branch = useMemo(() => (scope?.branches || []).find((b) => b.id === branchId), [scope, branchId]);

    useEffect(() => {
        (async () => {
            try {
                const r = await api.get("/branches/kpis/scope");
                setScope(r.data);
                const m = r.data.latest_month || todayIso().slice(0, 7);
                setMonth(m);
                setDate(lastDayOfMonth(m));
                const first = r.data.branches?.[0];
                if (first) setBranchId(first.id);
                if (!r.data.can_compare) setView("single");
            } catch (e) {
                toast.error(e?.response?.data?.detail || t("error_generic"));
            } finally {
                setLoading(false);
            }
        })();
    }, [t]);

    // keep the daily-revenue day inside the selected month — and never past today,
    // so the open month lands on a day that can actually have sales
    const lastDayInScope = useCallback((m) => {
        const end = lastDayOfMonth(m);
        // never past today, and never past the last day sales were actually pulled
        const cap = [scope?.coverage_to, todayIso()].filter(Boolean).sort()[0];
        if (!cap || cap >= end) return end;
        return cap >= `${m}-01` ? cap : end;
    }, [scope]);
    useEffect(() => {
        setDate((d) => (d && d.slice(0, 7) === month ? d : lastDayInScope(month)));
    }, [month, lastDayInScope]);

    const loadSingle = useCallback(async () => {
        if (!branchId || !month) return;
        try {
            const r = await api.get(`/branches/${branchId}/kpis`, { params: { month, date } });
            setSingle(r.data);
        } catch (e) {
            setSingle(null);
        }
    }, [branchId, month, date]);

    const loadCompare = useCallback(async () => {
        if (!month) return;
        try {
            const r = await api.get("/branches/kpis/compare", { params: { month, date } });
            setCompare(r.data);
        } catch (e) {
            setCompare(null);
        }
    }, [month, date]);

    useEffect(() => { if (view === "single") loadSingle(); }, [view, loadSingle]);
    useEffect(() => { if (view === "compare") loadCompare(); }, [view, loadCompare]);

    const onSort = (col) => {
        setSort((s) => (s.col === col ? { col, dir: s.dir === "asc" ? "desc" : "asc" } : { col, dir: "desc" }));
    };

    const sortedRows = useMemo(() => {
        const rows = [...(compare?.rows || [])];
        const { col, dir } = sort;
        const sign = dir === "asc" ? 1 : -1;
        rows.sort((a, b) => {
            if (col === "branch") {
                return sign * tn(a).localeCompare(tn(b), lang === "ar" ? "ar" : "en");
            }
            const av = a.kpis?.[col]?.available ? a.kpis[col].value : null;
            const bv = b.kpis?.[col]?.available ? b.kpis[col].value : null;
            if (av === null && bv === null) return 0;
            if (av === null) return 1;   // no-data always sinks
            if (bv === null) return -1;
            return sign * (av - bv);
        });
        return rows;
    }, [compare, sort, tn, lang]);

    const noCells = (kpis) => Object.values(kpis || {}).every((c) => !c?.available);
    const singleEmpty = !!single && noCells(single.kpis);
    const compareEmpty = !!compare && (compare.rows || []).every((r) => noCells(r.kpis));

    if (loading) return <div className="p-4"><Skeleton className="h-80 w-full" /></div>;

    if (!scope?.branches?.length) {
        return (
            <div data-testid={C.page}>
                <EmptyState testId={C.empty} icon={Store} title={t("bkpi_no_branch")} hint={t("bkpi_no_branch_hint")} />
            </div>
        );
    }

    const controls = (
        <>
            {view === "single" && (
                isBranchManager && scope.branches.length === 1 ? (
                    <div className="inline-flex items-center gap-2 rounded-full surface-2 border border-[color:var(--border-soft)] px-3 h-9 text-sm font-medium text-[color:var(--ink)]">
                        <Store className="h-4 w-4 text-[color:var(--brand-brown)]" />
                        {tn(branch)}
                    </div>
                ) : (
                    <Select value={branchId} onValueChange={setBranchId}>
                        <SelectTrigger data-testid={C.branchSelect} className="w-52"><SelectValue /></SelectTrigger>
                        <SelectContent>
                            {scope.branches.map((b) => (
                                <SelectItem key={b.id} value={b.id}>{tn(b)}</SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                )
            )}
            <Select value={month} onValueChange={setMonth}>
                <SelectTrigger data-testid={C.monthSelect} className="w-40"><SelectValue /></SelectTrigger>
                <SelectContent>
                    {months.map((m) => <SelectItem key={m} value={m}>{monthLabel(m, lang)}</SelectItem>)}
                </SelectContent>
            </Select>
            <div className="inline-flex items-center gap-1.5">
                <span className="text-xs text-[color:var(--ink-muted)] whitespace-nowrap">{t("bkpi_day")}</span>
                <Input
                    data-testid={C.dateInput}
                    type="date"
                    value={date}
                    min={`${month}-01`}
                    max={lastDayInScope(month)}
                    onChange={(e) => setDate(e.target.value)}
                    className="w-40"
                />
            </div>
        </>
    );

    return (
        <div data-testid={C.page} className="space-y-5">
            <PageHeader icon={BarChart3} title={t("bkpi_title")} subtitle={t("bkpi_subtitle")} actions={controls} />

            {scope.can_compare ? (
                <Tabs value={view} onValueChange={setView}>
                    <TabsList>
                        <TabsTrigger value="single" data-testid={C.viewSingle}>{t("bkpi_view_single")}</TabsTrigger>
                        <TabsTrigger value="compare" data-testid={C.viewCompare}>{t("bkpi_view_compare")}</TabsTrigger>
                    </TabsList>
                </Tabs>
            ) : null}

            {single?.month_source === "daily_rollup" && single?.month_through && !singleEmpty ? (
                <div
                    data-testid="bkpi-mtd-note"
                    className="flex flex-wrap items-center gap-x-2 gap-y-1 rounded-lg border border-[color:var(--border-soft)] bg-[color:var(--bg-surface-2)] px-3 py-2 text-xs"
                >
                    <Clock3 className="h-3.5 w-3.5 text-[color:var(--ink-faint)]" />
                    <span className="font-semibold text-[color:var(--ink-muted)]">
                        {t("bkpi_mtd_through")} <bdi dir="ltr">{single.month_through}</bdi>
                    </span>
                    <span className="text-[color:var(--ink-faint)]">· {t("bkpi_note_partial_month")}</span>
                </div>
            ) : null}

            {view === "single" && singleEmpty ? (
                <EmptyState testId={C.monthGap} icon={BarChart3} dataset="sales_daily_branch" month={month} />
            ) : view === "single" ? (
                <div className="grid gap-3 grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
                    {meta.map((m) => (
                        <KpiCard key={m.key} metaItem={m} cell={single?.kpis?.[m.key]} t={t} lang={lang} />
                    ))}
                </div>
            ) : compareEmpty ? (
                <EmptyState testId={C.monthGap} icon={BarChart3} dataset="sales_daily_branch" month={month} />
            ) : (
                <Card className="rounded-2xl border-[color:var(--border-soft)] shadow-[var(--shadow-sm)] overflow-hidden">
                    <div className="overflow-x-auto scrollbar-thin">
                        <Table data-testid={C.table}>
                            <TableHeader>
                                <TableRow>
                                    <SortHeader colKey="branch" label={t("bkpi_branch_col")} sort={sort} onSort={onSort} testId={C.colHeader("branch")} align="start" />
                                    {meta.map((m) => (
                                        <SortHeader key={m.key} colKey={m.key} label={t(`bkpi_kpi_${m.key}`)} sort={sort} onSort={onSort} testId={C.colHeader(m.key)} />
                                    ))}
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {sortedRows.map((r) => (
                                    <TableRow key={r.branch_id} data-testid={C.row(r.branch_id)}>
                                        <TableCell className="whitespace-nowrap font-medium text-[color:var(--ink)] text-start">{tn(r)}</TableCell>
                                        {meta.map((m) => {
                                            const cell = r.kpis?.[m.key];
                                            const display = fmtCell(cell, m.unit, lang);
                                            const flagged = cell?.note === "pos_not_carrying_discounts";
                                            return (
                                                <TableCell key={m.key} data-testid={C.cell(r.branch_id, m.key)} className="tnum text-end whitespace-nowrap">
                                                    {display === null ? (
                                                        <span title={t("bkpi_no_data_reason")} className="text-[color:var(--ink-faint)]">—</span>
                                                    ) : (
                                                        <span title={flagged ? t("bkpi_note_pos_not_carrying_discounts") : (cell?.source || "")} className={flagged ? "text-[color:var(--brand-brown)]" : "text-[color:var(--ink)]"}>
                                                            {display}{flagged ? " *" : ""}
                                                        </span>
                                                    )}
                                                </TableCell>
                                            );
                                        })}
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </div>
                </Card>
            )}

            {/* honesty legend */}
            <Card className="rounded-2xl border-[color:var(--border-soft)] bg-[color:var(--surface-2)]">
                <CardContent className="p-4 flex items-start gap-2.5">
                    <Info className="h-4 w-4 mt-0.5 shrink-0 text-[color:var(--brand-sky)]" />
                    <div>
                        <p className="text-sm font-semibold text-[color:var(--ink)]">{t("bkpi_legend_title")}</p>
                        <p className="text-xs text-[color:var(--ink-muted)] mt-0.5 leading-relaxed">{t("bkpi_legend_body")}</p>
                    </div>
                </CardContent>
            </Card>
        </div>
    );
}
